/**
 * Shared state and optics math for the compound scope.
 *
 * The scope is driven from two places at once - the Live Data panel (sliders and
 * the scope graphic) and the zoom window (the circular image) - so the settings
 * live here rather than in either component.
 *
 * Lifetime rules from the spec:
 *  - The eye piece ideal and position are picked once when the lab loads and are
 *    kept for as long as Microscopy is open, so a student who found the right
 *    separation keeps it while moving between images.
 *  - Focus and brightness are re-randomized every time a different image is put
 *    under the scope.
 */
import { reactive } from 'vue';

export const MAGNIFICATIONS = [4, 10, 40];
export const DEFAULT_MAGNIFICATION_INDEX = 1; // x10

const BRIGHT_SLICE_COUNT = 10;
// The eye piece graphics run 00 (closest together) to 10 (farthest apart).
const SPACE_SLICE_COUNT = 10;
// The stage graphics run 0 (lowest) to 2 (highest) for each magnification.
const FOCUS_SLICE_COUNT = 3;

function randomBetween(min, max) {
  return min + Math.random() * (max - min);
}

function clamp(value, min, max) {
  return Math.max(min, Math.min(max, value));
}

export const scope = reactive({
  mediaId: null,
  eyePieceIdeal: randomBetween(-0.75, 0.75),
  eyePiecePos: randomBetween(-0.5, 0.5),
  magnificationIndex: DEFAULT_MAGNIFICATION_INDEX,
  focusIdeal: 0,
  focusPos: 0,
  brightnessIdeal: 0,
  brightnessPos: 0,
  // How far the specimen has been dragged under the objective, in pixels.
  panX: 0,
  panY: 0,
  // Show the upscaled copy of the slide where one exists.
  useUpscaled: true,
});

/**
 * Re-rolls the settings that belong to a single image. The ideal values are what
 * the student is hunting for; the positions are where the slider starts out.
 */
export function randomizeImageSettings() {
  scope.focusIdeal = randomBetween(-0.75, 0.75);
  scope.focusPos = randomBetween(-0.5, 0.5);
  scope.brightnessIdeal = randomBetween(0.75, 0.9);
  scope.brightnessPos = randomBetween(0.5, 1);
}

randomizeImageSettings();

/**
 * Puts an image under the scope. Focus and brightness are reset for a new image;
 * magnification and eye piece separation are left alone.
 */
export function useImage(mediaId) {
  if (mediaId == null || mediaId === scope.mediaId) {
    return;
  }
  scope.mediaId = mediaId;
  resetPan();
  randomizeImageSettings();
}

/** Re-centres the specimen. */
export function resetPan() {
  scope.panX = 0;
  scope.panY = 0;
}

/** Current magnification as a number: 4, 10, 40 or 100. */
export function magnification() {
  return MAGNIFICATIONS[clamp(scope.magnificationIndex, 0, MAGNIFICATIONS.length - 1)];
}

/**
 * How much larger the specimen is drawn than at the lowest objective, where it
 * exactly fills the field. The field of view terms of the original equation
 * cancel out of this ratio, leaving it proportional to the magnification.
 */
export function magnificationZoom() {
  return magnification() / MAGNIFICATIONS[0];
}

/**
 * Distance between the two eye piece images, where 0 is aligned and 1 means the
 * edge of each image sits on the centre of the other. Can exceed 1.
 */
export function separation() {
  return Math.abs(scope.eyePiecePos - scope.eyePieceIdeal);
}

/** Blur to apply, 0 (sharp) to 1 (maximum). */
export function blurAmount() {
  return Math.abs(scope.focusIdeal - scope.focusPos);
}

/** Signed brightness adjustment: negative is darker than default. */
export function brightnessAmount() {
  return scope.brightnessPos - scope.brightnessIdeal;
}

/** Index 00-10 of the eye piece graphic: slider full left is farthest apart. */
export function spaceSliceIndex() {
  const normalized = (1 - clamp(scope.eyePiecePos, -1, 1)) / 2;
  return Math.round(normalized * SPACE_SLICE_COUNT);
}

/** 1-4, matching the CompScope_Mag_<n> graphics. */
export function magSliceNumber() {
  return clamp(scope.magnificationIndex, 0, MAGNIFICATIONS.length - 1) + 1;
}

/** 0-2 stage height, low to high, following the focus slider. */
export function focusSliceIndex() {
  const normalized = (clamp(scope.focusPos, -1, 1) + 1) / 2;
  return clamp(Math.floor(normalized * FOCUS_SLICE_COUNT), 0, FOCUS_SLICE_COUNT - 1);
}

/** 1-10 for the lamp graphic, or 0 when the light is off. */
export function brightSliceIndex() {
  return Math.round(clamp(scope.brightnessPos, 0, 1) * BRIGHT_SLICE_COUNT);
}
